import { supabase } from '@/lib/supabase';
import type {
  ModelLayout,
  ResyncResponse,
  DiffsResponse,
  ModelEditRequest,
  TableEditRequest,
  ViewsResponse,
  MarketDefaultSuggestionItem,
} from '@arcanum-hq/shared-types';
import {
  resyncResponseSchema,
  diffsResponseSchema,
} from '@arcanum-hq/shared-types';

const API_URL = import.meta.env.VITE_API_URL || '';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

/**
 * One-shot guard so a burst of concurrent 401s triggers a single local
 * sign-out, not a storm. Re-armed after the next successful authenticated
 * response (i.e. once the user is genuinely signed in again).
 */
let unauthorizedHandled = false;

/**
 * Reconcile a server-rejected session with the client on a 401.
 *
 * A 401 on a request that DID carry a Bearer token means the server rejected
 * our session — most often because the refresh token was revoked server-side
 * (e.g. a global sign-out on another device). `verifyToken`
 * (`packages/supabase/src/lib/auth.ts`) calls `auth.getUser(token)`
 * server-side, so the rejection lands IMMEDIATELY — even while the access
 * token's `exp` hasn't passed and the client still holds the session in
 * localStorage. That stale-but-cached session keeps `useAuth().user` truthy,
 * so the `__root` redirect cascade can't detect "logged out": admin-status +
 * billing both fail-closed (401 → `isAdmin:false` / no balance row) and the
 * cascade parks the user on `/billing/checkout` instead of `/login`.
 *
 * Clearing the LOCAL session (`scope:'local'` — NEVER `global`, which would
 * compound the revocation that caused this) flips `user` to null on the next
 * `onAuthStateChange`; the cascade then routes to `/login`.
 *
 * A 401 with NO Bearer token is an anonymous request hitting an auth-only
 * endpoint (e.g. a public deal-link viewer) — expected; leave the session
 * untouched.
 */
function reconcileUnauthorized(hadAuthToken: boolean): void {
  if (!hadAuthToken || unauthorizedHandled) return;
  unauthorizedHandled = true;
  try {
    // Optional-call `signOut` — real clients always have it, but partial test
    // mocks may omit it; never let session-reconciliation break the error path.
    void Promise.resolve(supabase.auth.signOut?.({ scope: 'local' })).catch(
      () => {
        // Best-effort — re-arm so a later 401 can retry the local clear.
        unauthorizedHandled = false;
      }
    );
  } catch {
    unauthorizedHandled = false;
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: Record<string, unknown>,
    /** Parsed `Retry-After` (seconds) on 429 responses, when the server sends it. */
    public retryAfterSeconds?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Structured payload returned with HTTP 410 `MODEL_VERSION_REMOVED` and the
 * matching SSE error event. The Builder's MissingModelPanel reads this to
 * render the fork/delete recovery flow. `latestAvailable: null` means there's
 * no other registered version of the model — switch to the destructive branch.
 */
export interface MissingModelDetails {
  modelId: string;
  modelVersion: string;
  latestAvailable: string | null;
}

/**
 * Subset of the model schema surfaced through `GET /model-view`. Field types
 * are `unknown[]` because the wire shape is unknown to the client (the
 * authoritative ModelSchema lives in `@arcanum-hq/shared-types`); the grid
 * narrows on demand inside `formatCellValue` and similar helpers.
 *
 * Locally declared (NOT imported from shared-types runtime) because the API
 * package's CLAUDE.md "no Zod-runtime imports from shared-types" rule applies
 * to the web package too — vitest can't transform the ESM dist without
 * thrashing the test bootstrap. Type-only imports remain safe.
 */
export interface ModelViewSchemaSubset {
  inputs: unknown[];
  outputs: unknown[];
  dependencyGraph: unknown[];
  tableInputs: unknown[];
  allNamedRanges: unknown[];
  /**
   * `deal-signal-overhaul` Phase 5 hotfix (2026-05-13). Projected by the
   * shared model-view backend ONLY (the auth conversations.service.v2's
   * `getModelView` did not need it because auth web already calls
   * `useModel(modelId)`). Public viewers consume this directly via
   * `useSignalFieldResolver`'s public path to resolve `signalFieldMappings`
   * without hitting `/api/models/:id` (auth-only → 401 for unauth viewers
   * → permanent `data-state="loading"` on all 5 Signal regions).
   *
   * Optional + nullable — auth `getModelView` callers see `undefined` here.
   */
  displayConfig?: unknown;
  signal_variant?: 'operating-asset' | 'new-development' | null;
}

/**
 * Happy-path response for `GET /api/conversations/:id/model-view` —
 * full renderable Model tab payload.
 *
 * `layoutMissing?: false` on the happy variant lets consumers narrow the
 * `ModelViewPayload` union via a single `data.layoutMissing` check (TS
 * accepts the property access on both variants and narrows correctly).
 */
export interface ModelViewResponse {
  layout: ModelLayout;
  layoutMissing?: false;
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  provenance: Record<string, unknown>;
  schema: ModelViewSchemaSubset;
  confirmedRevision: number;
}

/**
 * Layout-missing branch (HTTP 200) — conversation pinned to a pre-layout
 * `(modelId, modelVersion)`. The web side renders `<LayoutUnavailablePanel>`
 * with a "start new conversation on `<latestVersionWithLayout>`" CTA when
 * that field is non-null; otherwise a contact-admin fallback.
 */
export interface ModelViewMissingLayoutResponse {
  layout: null;
  layoutMissing: true;
  modelId: string;
  modelVersion: string;
  latestVersionWithLayout: string | null;
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  provenance: Record<string, unknown>;
  schema: ModelViewSchemaSubset;
  confirmedRevision: number;
}

export type ModelViewPayload =
  | ModelViewResponse
  | ModelViewMissingLayoutResponse;

/**
 * Response from `POST /api/conversations/:id/model-edit` (Phase 2 backend
 * shipped this shape; Phase 4 Session 3 web wires the caller). The wire
 * `appliedOps` / `computedChanges` / `provenance` payloads have rich types
 * in `@arcanum-hq/shared-types/lib/realtime.ts` (`DiffOp`, `CellOp`,
 * `ComputedChange`); we narrow on demand at the call site rather than
 * importing Zod-runtime values into web (per the existing project rule).
 */
export interface ModelEditResponse {
  editId: string;
  revision: number;
  prevRevision: number;
  appliedOps: unknown[];
  computedChanges: unknown[];
  appliedAt: string;
  systemMessageId: string | null;
  provenance: unknown;
  /**
   * 2026-05-26 — full optimistic-patch response envelope. Server commits
   * inputProvenance + dealMetadata + aerialLatLng synchronously (location
   * resolution awaited inline) and returns the post-edit state so the web
   * cache can patch synchronously without a refetch round-trip. Closes the
   * "Property location — not set" flash that previously appeared between
   * the model-edit POST returning and the views refetch landing.
   */
  stateDelta: {
    written: Record<string, unknown>;
    recomputed: Record<string, unknown>;
    currentProvenance: Record<
      string,
      { source: string; defaultStatus?: string | null; confidence?: number }
    >;
    pendingDefaults: string[];
  };
  metadataChanges: unknown[];
  /** Full ProvenanceRecord map — edited input + derived sub-attrs (Location.*). */
  inputProvenance: Record<string, unknown>;
  /** Post-edit dealMetadata map; non-null for Location edits. */
  dealMetadata?: Record<string, unknown> | null;
  /** Post-edit aerialLatLng; non-null for Location edits with resolved coords. */
  aerialLatLng?: { lat: number; lng: number } | null;
}

/**
 * Web-local mirror of the supabase `CacheRow` shape (web does NOT import
 * `@arcanum-hq/supabase` — Cross-Domain Contract). The `data` field stays
 * `unknown` because per-category shape varies; no per-category Zod contract
 * is imposed at the web layer (ADR 0006, research §11 risk 11).
 */
export interface MarketDataCacheRow {
  cache_key: string;
  source: string;
  category: string;
  metro: string | null;
  zip_code: string | null;
  property_type: string | null;
  data: unknown;
  fetched_at: string;
  valid_until: string;
}

/**
 * Subclass of `ApiError` thrown when an API call returns HTTP 410 with code
 * `MODEL_VERSION_REMOVED`. Existing `instanceof ApiError` handlers continue
 * to catch it; downstream code that wants the typed `missing` payload can
 * narrow with `instanceof MissingModelError`.
 */
export class MissingModelError extends ApiError {
  public readonly missing: MissingModelDetails;

  constructor(message: string, missing: MissingModelDetails) {
    super(
      410,
      message,
      'MODEL_VERSION_REMOVED',
      missing as unknown as Record<string, unknown>
    );
    this.name = 'MissingModelError';
    this.missing = missing;
    Object.setPrototypeOf(this, MissingModelError.prototype);
  }
}

interface ApiErrorBody {
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
  error?: {
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
  };
}

function buildApiError(
  status: number,
  body: ApiErrorBody,
  fallbackMessage: string,
  retryAfterSeconds?: number
): ApiError {
  const code = body.error?.code ?? body.code;
  const message = body.error?.message ?? body.message ?? fallbackMessage;
  const details = body.error?.details ?? body.details;
  if (status === 410 && code === 'MODEL_VERSION_REMOVED' && details) {
    return new MissingModelError(
      message,
      details as unknown as MissingModelDetails
    );
  }
  return new ApiError(status, message, code, details, retryAfterSeconds);
}

/** Parse a `Retry-After` header (delta-seconds form) → number | undefined. */
function parseRetryAfter(res: Response): number | undefined {
  // Optional-chain `headers` — real Responses always have it, but partial test
  // mocks may omit it; never let header-reading break the error path.
  const raw = res.headers?.get?.('retry-after');
  if (!raw) return undefined;
  const seconds = Number.parseInt(raw, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    // `credentials: 'include'` so cookies set by the API (e.g. the
    // `viewer_session_id` cookie minted by `/api/shared/:shareId/questions`
    // for the public deal-link gate) round-trip on subsequent requests.
    // CORS already has `credentials: true` (see corsMiddleware), so the
    // browser accepts the response. Auth-bearing endpoints continue to
    // use the Bearer token; this is purely additive for cookie-based
    // session tracking. SHIPPED `agent-ux-optimization` Phase 6 Session 6.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Arcanum-Protocol': 'v2',
      ...authHeaders,
      ...options.headers,
    },
  });

  // 304 Not Modified — Express's default ETag flow returns this when the
  // browser sends If-None-Match and the response hasn't changed. fetch's
  // res.ok is false for 3xx, but res.json() works because the browser
  // auto-fills the body from its HTTP cache. Treating 304 as success is
  // necessary for any GET that gets called more than once per session.
  if (!res.ok && res.status !== 304) {
    if (res.status === 401) reconcileUnauthorized(!!authHeaders.Authorization);
    const body: ApiErrorBody = await res
      .json()
      .catch(() => ({ message: res.statusText }));
    throw buildApiError(res.status, body, res.statusText, parseRetryAfter(res));
  }

  // Successful authenticated response → re-arm the 401 guard so a future
  // session revocation is handled again after the user signs back in.
  if (res.ok && authHeaders.Authorization) unauthorizedHandled = false;

  // 204 No Content — return empty object instead of trying to parse JSON
  if (res.status === 204) return {} as T;

  return res.json();
}

/**
 * Unwrap the `{ success, data }` envelope produced by `sendSuccess`.
 * Server-side every successful response wraps the payload; this helper lets
 * callers declare the inner shape.
 */
function unwrapEnvelope<T>(envelope: unknown): T {
  if (
    envelope &&
    typeof envelope === 'object' &&
    'data' in envelope &&
    (envelope as { success?: boolean }).success === true
  ) {
    return (envelope as { data: T }).data;
  }
  // Pre-envelope handlers (legacy) returned the payload directly — accept it.
  return envelope as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  // --------------------------------------------------------------------------
  // Realtime subscriber REST helpers — realtime-collab-foundation Phase 4
  // Session 3. Thin wrappers over `post` / `get` with runtime validation via
  // the Phase 1 Zod schemas, so the hook can trust the wire shape.
  // --------------------------------------------------------------------------

  /** POST /api/conversations/:id/resync */
  resync: async (
    conversationId: string,
    lastRevision: number
  ): Promise<ResyncResponse> => {
    const envelope = await request<unknown>(
      `/api/conversations/${conversationId}/resync`,
      {
        method: 'POST',
        body: JSON.stringify({ lastRevision }),
      }
    );
    const payload = unwrapEnvelope<unknown>(envelope);
    return resyncResponseSchema.parse(payload);
  },

  /**
   * GET /api/conversations/:id/model-view — in-platform-model-view Phase 2
   * consumer for the Model tab. Returns either the full renderable payload
   * (with parsed `layout`) or the layout-missing fallback (`layout: null,
   * layoutMissing: true`), discriminated on the `layoutMissing` literal. The
   * 410 `MODEL_VERSION_REMOVED` path surfaces as a thrown `MissingModelError`
   * (matches `getConversation` semantics — caller renders the recovery flow).
   */
  getModelView: async (conversationId: string): Promise<ModelViewPayload> => {
    const envelope = await request<unknown>(
      `/api/conversations/${conversationId}/model-view`
    );
    return unwrapEnvelope<ModelViewPayload>(envelope);
  },

  /**
   * GET /api/conversations/:id/views — builder-ssot-consolidation Phase 5
   * Session 5.1. The unified SSOT projection: inputs (ProvenanceRecord map),
   * cellValues, modelLayout, signal, dealMetadata, aerialLatLng, meta.
   * NOT enveloped — the endpoint returns the bare ViewsResponse directly
   * (unlike /model-view which wraps in {success,data}). ADR 0017.
   */
  getViews: (conversationId: string): Promise<ViewsResponse> =>
    request<ViewsResponse>(`/api/conversations/${conversationId}/views`),

  /**
   * GET /api/market-data/cache?category=<c>&metro=<m>
   * Durable read of the service-role-only market_data_cache for the picker
   * one-click apply affordance (ADR 0004 + 0006). Returns unwrapped
   * MarketDataCacheRow[] from the {success,data} envelope (mirrors getModelView
   * — the /views bare path is the WRONG model here; research §11 ENVELOPE
   * COLLISION, F03 failure mode).
   */
  getMarketDataCache: async (
    category: string,
    metro: string
  ): Promise<MarketDataCacheRow[]> => {
    const qs = new URLSearchParams({ category, metro }).toString();
    const envelope = await request<unknown>(`/api/market-data/cache?${qs}`);
    return unwrapEnvelope<MarketDataCacheRow[]>(envelope);
  },

  /**
   * POST /api/conversations/:id/model-edit — in-platform-model-view Phase 2
   * shipped the backend; Phase 4 Session 3 wires the web caller. Body is
   * `{ namedRange, value, editId, placeId?, sessionToken? }` where `editId` is
   * a UUIDv4 generated client-side via `crypto.randomUUID()` (matches
   * `modelEditRequestSchema` at `packages/shared-types/src/lib/api.types.ts`).
   * The optional `placeId`/`sessionToken` are the ADR-0027 location-resolve
   * fields, only populated for a `dataType:'location'` input set. Six error
   * reasons surface as typed `ApiError`s with `code`:
   *   - 400 EMPTY_EDIT / UNKNOWN_INPUT / NORMALIZE_FAILED / VALIDATION_FAILED
   *   - 500 ENGINE_ERROR
   *   - 410 MODEL_VERSION_REMOVED (mirrors `getModelView`)
   * Idempotent replays return 200 with `systemMessageId: null`.
   *
   * Phase 4 Session 3 invariant: callers MUST NOT mutate `cellValues` or
   * invalidate the `modelView` query on success — the canonical update
   * path is the Realtime echo via Session 4's bridge ref.
   */
  postModelEdit: async (
    conversationId: string,
    body: ModelEditRequest
  ): Promise<ModelEditResponse> => {
    const envelope = await request<unknown>(
      `/api/conversations/${conversationId}/model-edit`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return unwrapEnvelope<ModelEditResponse>(envelope);
  },

  /**
   * POST /api/conversations/:id/table-edit — table-input-ux (2026-06-08).
   * Writes a full table grid via `recordEdit` (no LLM round-trip).
   * Body: `{ table, rows: [{label, values}], editId }`.
   * Response: `ModelEditResponse` — `stateDelta.written` carries named-range keys
   * (e.g. `Studio_Rent_Month`), NOT display paths (design §7).
   * Envelope handling mirrors `postModelEdit` exactly (ADR-0017).
   */
  postTableEdit: async (
    conversationId: string,
    body: TableEditRequest
  ): Promise<ModelEditResponse> => {
    const envelope = await request<unknown>(
      `/api/conversations/${conversationId}/table-edit`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return unwrapEnvelope<ModelEditResponse>(envelope);
  },

  /**
   * POST /api/conversations/:id/default-match-suggestions
   * market-default-apply-ux Phase 2 Session 2.5 (DNE-4).
   *
   * Requests Haiku-generated input-match suggestions for a single market field
   * value. Ownership-gated (user's own open conversation only — ADR 0006).
   * Response is enveloped `{success,data}` → unwrapEnvelope extracts `{suggestions}`.
   * Body MUST match DefaultMatchSuggestionsBodySchema on the server:
   *   marketField, value, unit?, eligibleInputs[{name,displayName,dataType,description?}]
   *
   * Callers should treat any thrown error as fail-open (empty suggestions) —
   * the `useDefaultMatchSuggestions` hook wraps this in a try/catch.
   */
  defaultMatchSuggestions: async (
    conversationId: string,
    body: {
      marketField: string;
      value: number;
      unit?: string;
      eligibleInputs: Array<{
        name: string;
        displayName: string;
        dataType: string;
        description?: string;
      }>;
    }
  ): Promise<{ suggestions: MarketDefaultSuggestionItem[] }> => {
    return unwrapEnvelope(
      await api.post(
        `/api/conversations/${conversationId}/default-match-suggestions`,
        body
      )
    );
  },

  /** GET /api/conversations/:id/diffs?since=N[&limit=M] */
  getDiffs: async (
    conversationId: string,
    since: number,
    limit?: number
  ): Promise<DiffsResponse> => {
    const qs = new URLSearchParams({ since: String(since) });
    if (limit !== undefined) qs.set('limit', String(limit));
    const envelope = await request<unknown>(
      `/api/conversations/${conversationId}/diffs?${qs.toString()}`
    );
    const payload = unwrapEnvelope<unknown>(envelope);
    return diffsResponseSchema.parse(payload);
  },

  /**
   * GET /api/conversations/:id/collection-state — next-variable-curation Phase 6.
   * Returns `{ collectionState: CollectionState }` server-computed from
   * persisted provenance (owner-gated, standard sendSuccess envelope). Use as
   * the durable fallback for the NEXT banner between turns and on load; the
   * live SSE carrier (latestToolPayloads.collectionState) takes precedence
   * while a stream is in-flight.
   */
  getCollectionState: async (
    conversationId: string
  ): Promise<{
    collectionState: import('@arcanum-hq/shared-types').CollectionState;
  }> => {
    const envelope = await request<unknown>(
      `/api/conversations/${conversationId}/collection-state`
    );
    return unwrapEnvelope<{
      collectionState: import('@arcanum-hq/shared-types').CollectionState;
    }>(envelope);
  },

  /** Upload multipart form data (model ingestion) */
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'X-Arcanum-Protocol': 'v2', ...authHeaders },
      body: formData,
    });
    if (!res.ok) {
      if (res.status === 401)
        reconcileUnauthorized(!!authHeaders.Authorization);
      const body: ApiErrorBody = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw buildApiError(res.status, body, res.statusText);
    }
    return res.json();
  },

  /**
   * Upload multipart form data with an SSE-streamed response. Heartbeats
   * from the server keep the Cloud Run Domain Mapping connection alive past
   * its ~15s idle cap. Resolves on the first `complete` event with its
   * `data` payload; rejects on `error` with a matching `ApiError`.
   */
  uploadStream: async <T>(path: string, formData: FormData): Promise<T> => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'X-Arcanum-Protocol': 'v2', ...authHeaders },
      body: formData,
    });

    if (!res.ok) {
      if (res.status === 401)
        reconcileUnauthorized(!!authHeaders.Authorization);
      const body: ApiErrorBody = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      throw buildApiError(res.status, body, res.statusText);
    }

    const reader = res.body?.getReader();
    if (!reader) throw new ApiError(500, 'No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        let evt: {
          type?: string;
          data?: unknown;
          code?: string;
          message?: string;
          details?: Record<string, unknown>;
        };
        try {
          evt = JSON.parse(trimmed.slice(6));
        } catch {
          continue;
        }
        if (!evt || typeof evt !== 'object') continue;
        if (evt.type === 'heartbeat' || evt.type === 'started') continue;
        if (evt.type === 'complete') return evt.data as T;
        if (evt.type === 'error') {
          throw buildApiError(
            400,
            { message: evt.message, code: evt.code, details: evt.details },
            evt.message ?? 'Stream error'
          );
        }
      }
    }

    throw new ApiError(500, 'Stream ended without complete or error event');
  },

  /**
   * POST /api/conversations/:id/messages/stream/abort — terminates an
   * in-flight LangGraph run server-side. The Phase 1 endpoint emits a
   * final `aborted` SSE event followed by `done` on the original stream;
   * this REST call is the trigger.
   *
   * Returns `{ found: true }` on 204 No Content, `{ found: false }` on 404
   * (no active run for the conversation — benign race when the user clicks
   * Stop just as the agent finishes naturally). Other errors propagate as
   * thrown ApiError.
   */
  abortStream: async (
    conversationId: string,
    reason: 'user' | 'timeout' | 'budget' = 'user'
  ): Promise<{ found: boolean }> => {
    try {
      await request<unknown>(
        `/api/conversations/${conversationId}/messages/stream/abort`,
        { method: 'POST', body: JSON.stringify({ reason }) }
      );
      return { found: true };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return { found: false };
      }
      throw err;
    }
  },

  /** Download file as blob */
  download: async (path: string): Promise<Blob> => {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`${API_URL}${path}`, {
      headers: { 'X-Arcanum-Protocol': 'v2', ...authHeaders },
    });
    if (!res.ok) {
      if (res.status === 401)
        reconcileUnauthorized(!!authHeaders.Authorization);
      throw new ApiError(res.status, res.statusText);
    }
    return res.blob();
  },

  /** Create SSE EventSource connection. Returns cleanup function. */
  stream: async (
    path: string,
    body: unknown,
    onEvent: (event: unknown) => void,
    onError: (error: Error) => void
  ): Promise<() => void> => {
    const authHeaders = await getAuthHeaders();
    const controller = new AbortController();

    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Arcanum-Protocol': 'v2',
        ...authHeaders,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!res.ok) {
      if (res.status === 401)
        reconcileUnauthorized(!!authHeaders.Authorization);
      const errBody: ApiErrorBody = await res
        .json()
        .catch(() => ({ message: res.statusText }));
      onError(buildApiError(res.status, errBody, res.statusText));
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const reader = res.body?.getReader();
    if (!reader) {
      onError(new Error('No response body'));
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const decoder = new TextDecoder();
    let buffer = '';

    (async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('data: ')) {
              try {
                const data = JSON.parse(trimmed.slice(6));
                onEvent(data);
              } catch {
                // skip malformed SSE lines
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          onError(err);
        }
      }
    })();

    return () => controller.abort();
  },
};
